import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';

import useUI from 'src/hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import { VisuallyHidden } from 'shared/components/visually-hidden';
import { formatCurrency, pluralize } from 'shared/helpers/utils';

import CloseIcon from 'shared/assets/exit-icon';
import { CartV2 } from 'src/assets/cart-v2';

import { useBanner } from './use-banner';
import { OrderType } from './order-type';
import { DeliveryEstimate } from './delivery-estimate';

export const CartHeader = observer(
  (): JSX.Element => {
    const { closeCartDrawer } = useUI();
    const { dispensary, isCalculating, itemCount, subtotal } = useCart();
    const { dispensary: fallbackDispensary } = useDispensary();
    const activeDispensary = dispensary ?? fallbackDispensary;
    const { banner } = useBanner();

    return (
      <Header data-testid='cart-header'>
        <Wrapper $hasBanner={banner}>
          <Row $alignItems='flex-start'>
            <Location data-testid='cart-header-location'>
              <VisuallyHidden>Shopping at:</VisuallyHidden>
              <Name data-testid='cart-header-dispensary-name'>{activeDispensary?.name}</Name>
              <Address data-testid='cart-header-dispensary-address'>{activeDispensary?.location?.ln1}</Address>
            </Location>

            <CloseButton data-testid='cart-close-button' onClick={closeCartDrawer}>
              <VisuallyHidden>Close</VisuallyHidden>
              <CloseIcon height={28} width={28} />
            </CloseButton>
          </Row>

          <Row $flexWrap>
            <Total data-testid='cart-header-total'>
              <CartIcon height={20} width={20} />
              {isCalculating ? (
                <div data-testid='cart-header-calculating'>Calculating…</div>
              ) : (
                <Info data-testid='cart-header-info'>
                  {itemCount > 0 && <Amount data-testid='cart-dropdown-subtotal'>{formatCurrency(subtotal)}</Amount>}
                  <div data-testid='cart-header-item-count'>
                    ( {itemCount} {pluralize('Item', itemCount)} )
                  </div>
                </Info>
              )}
            </Total>

            <OrderType dispensary={activeDispensary} />

            <DeliveryEstimate />
          </Row>
        </Wrapper>

        {banner}
      </Header>
    );
  }
);

const Header = styled.header`
  color: #121516;
  position: sticky;
  width: 100%;
`;

const Wrapper = styled.div<{ $hasBanner?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  left: 0;
  padding: 20px 16px;
  width: 100%;

  ${({ $hasBanner }) =>
    !$hasBanner &&
    css`
      border-bottom: 1px solid #e3e7e9;
    `}
`;

const Row = styled.div<{ $alignItems?: string; $flexWrap?: boolean }>`
  align-items: ${({ $alignItems = 'center' }) => $alignItems};
  display: flex;
  justify-content: space-between;
  width: 100%;

  ${({ $flexWrap }) =>
    $flexWrap &&
    css`
      flex-wrap: wrap;
    `}
`;

const Location = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  line-height: 1;
  padding-left: 16px;
  position: relative;

  &::before {
    background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
    border-radius: ${({ theme }) => theme.customized.radius.sm};
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 4px;
  }
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Address = styled.div`
  font-size: 14px;
`;

const CloseButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: #485055;
  cursor: pointer;
  padding: 0;

  svg {
    display: block;
    fill: currentColor;
  }
`;

const CartIcon = styled(CartV2)`
  color: #485055;

  path {
    vector-effect: non-scaling-stroke;
  }
`;

const Total = styled.div`
  align-items: center;
  display: flex;
  font-size: 14px;
  gap: 12px;
  line-height: 1;
`;

const Info = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const Amount = styled.div`
  font-size: 20px;
  font-weight: 700;
`;
